import { GroupOfSelectable } from "@tenforce/product-fields"
import {
    FieldType,
    IColumnDefinition,
    IItem,
    IItemsCollection,
    IThumbTableDefinition,
} from "../components/thumbtableview"
import { dataAPI } from "./api"
import {
    createSelectorGroups,
    LoadChildrenResponse,
    parseGroupData,
    parseSelectorData,
} from "./selectors"
import { parseFieldData } from "./utils/fieldparsing"
import { ODataGetArrayResponse } from "./models/api/data"
import {
    CollectionDefinition,
    CollectionItemView,
    OptionFieldDefinition,
    TextFieldDefinition,
    VisibleColumnsDefinition,
} from "./models/thumbtable"
import { IField } from "../components/fields"
import { IUserFormattingSettings } from "../components/applicationheader/components/usermenu"
import {
    AggregateResponse,
    FieldDefinitionDTO,
    TypeModel,
} from "./models/selectors/models"
import { RequestErrorDataApi } from "../errors"
import { Exceptions } from "../util/Translations"

const { Error = "An error occurred." } = Exceptions

export const getTableData = (
    selectionFilterId: number,
): Promise<IThumbTableDefinition> =>
    dataAPI
        .getJSON<CollectionDefinition>(
            `ItemCollections(${selectionFilterId})/Query`,
        )
        .then((collectionDefinition) => {
            return {
                name: collectionDefinition.Label,
                code: collectionDefinition.Code,
                columns: parseColumns(collectionDefinition.Fields),
            } as IThumbTableDefinition
        })

export const getTableDataAsSelectables = (
    selectionFilterId: number,
): Promise<GroupOfSelectable | null> =>
    dataAPI
        .getJSON<VisibleColumnsDefinition>(
            `ItemCollections(${selectionFilterId})/Query`,
        )
        .then((response) => {
            return createSelectorGroups(response.Fields, "TF.FieldDefinition")
        })

const parseColumns = (columns: FieldDefinitionDTO[]): IColumnDefinition[] => {
    return columns.map(
        (field) =>
            ({
                code: field.Code,
                fieldId: field.Id,
                label: field.Label,
                fieldType: parseFieldType(field),
            } as IColumnDefinition),
    )
}

export const getSelectedColumns = async (
    collectionId: number,
): Promise<string[]> => {
    return (await getTableData(collectionId)).columns.map(
        (column) => column.fieldId,
    )
}

export const getColumnGroups = async (
    filter: string,
): Promise<GroupOfSelectable | null> => {
    const applyQuery = filter
        ? `filter(contains(Label,'${filter}'))/groupby((Group/Label,Group/Id),aggregate($count as Total))`
        : "groupby((Group/Label,Group/Id),aggregate($count as Total))"

    return dataAPI
        .getJSON<
            ODataGetArrayResponse<
                TypeModel["TF.FieldDefinition"] & AggregateResponse
            >
        >("FieldDefinitions", { $apply: applyQuery })
        .then((response) => {
            return parseGroupData(response, "TF.FieldDefinition")
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                throw await RequestErrorDataApi.create(
                    Error,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const getColumnsForGroup = async (
    groupId: number,
    filter = "",
    skip = 0,
    top = 20,
): Promise<LoadChildrenResponse> => {
    const filterQuery = `contains(Label,'${filter}')`
    const parsedGroupId = groupId > 0 ? `CFG${groupId}` : `SFG${groupId * -1}`
    const filterByGroup =
        groupId !== 0 ? `Group/Id eq '${parsedGroupId}'` : "Group eq null"

    const parameters = {
        $filter: filter
            ? `${filterByGroup} and ${filterQuery}`
            : `${filterByGroup}`,
        $top: top,
        $skip: skip,
        $expand: "Group",
        $count: true,
    }

    return dataAPI
        .getJSON<ODataGetArrayResponse<TypeModel["TF.FieldDefinition"]>>(
            "FieldDefinitions",
            parameters,
        )
        .then((response) => {
            return {
                children: parseSelectorData(
                    response.value,
                    "TF.FieldDefinition",
                ),
                count: response["@count"],
            } as LoadChildrenResponse
        })
}

export const getItems = async (
    collectionId: number,
    userFormattingSettings: IUserFormattingSettings,
    skip = 0,
    top = 20,
): Promise<IItemsCollection> => {
    const parameters = {
        $top: top,
        $skip: skip,
        $count: true,
    }

    return dataAPI
        .getJSON<ODataGetArrayResponse<CollectionItemView>>(
            `ItemCollections(${collectionId})/Query/View()`,
            parameters,
        )
        .then((response) => {
            const mappedItems = response.value.map((itemView) => {
                const mappedFields = itemView.Components.map((component) => {
                    return {
                        ...parseFieldData(component, userFormattingSettings),
                        description: "",
                        title: "",
                    } as IField
                })
                return {
                    id: itemView.ItemId,
                    fields: mappedFields,
                } as IItem
            })

            return {
                items: mappedItems,
                count: response["@count"],
            } as IItemsCollection
        })
}

export const parseFieldType = (component: FieldDefinitionDTO): FieldType => {
    const odataType = component["@type"]
    switch (odataType) {
        case "#TF.TextFieldDefinition": {
            const { TextFieldType } = component as TextFieldDefinition
            return TextFieldType as FieldType
        }
        case "#TF.TimeFieldDefinition":
            return "Time"
        case "#TF.NumberFieldDefinition":
            return "Number"
        case "#TF.BoolFieldDefinition":
            return "Checkbox"
        case "#TF.DateTimeOffsetFieldDefinition":
        case "#TF.DateFieldDefinition":
            return "Date"
        case "#TF.StatusFieldDefinition":
        case "#TF.PriorityFieldDefinition":
        case "#TF.UserFieldDefinition":
        case "#TF.UserGroupFieldDefinition":
        case "#TF.OrganizationUnitFieldDefinition":
        case "#TF.ApplicationFieldDefinition":
        case "#TF.ApplicationVersionFieldDefinition":
        case "#TF.ItemFieldDefinition":
            return "SingleSelect"
        case "#TF.OptionFieldDefinition": {
            const { OptionFieldType } = component as OptionFieldDefinition
            return OptionFieldType === "Toggle" ? "Toggle" : "SingleSelect"
        }
        case "#TF.UsersFieldDefinition":
        case "#TF.ItemsFieldDefinition":
        case "#TF.OptionsFieldDefinition":
            return "MultiSelect"
        case "#TF.TagsFieldDefinition":
            return "Tags"
        case "#TF.RtfConceptViewFieldComponent":
            return "RTF"
        case "#TF.AttachmentsConceptViewFieldComponent":
            return "Attachment"
        case "#TF.LocationFieldDefinition":
        case "#TF.ConceptFieldDefinition":
        case "#TF.AnswerFieldDefinition":
        case "#TF.ExplanatoryFieldDefinition":
            return "Text"
        case "#TF.SignatureFieldDefinition":
            return "Signature"
        default:
            return "Default"
    }
}
